import React, { useState, useEffect } from 'react';
import { orderService, productService } from '../../services/api';
import Loading from '../../components/Loading';
import html2pdf from 'html2pdf.js';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const DELIVERY_PRICE = 5000; // Default delivery price in IQD

  // Calculate delivery cost based on total weight
  const calculateDeliveryPrice = (items) => {
    const totalWeight = items.reduce((sum, item) => {
      const weight = parseFloat(item.weight) || 1; // Default 1kg if no weight
      return sum + (weight * item.quantity);
    }, 0);

    let cost = 5000; // Minimum delivery cost
    if (totalWeight > 5) {
      const additionalWeight = Math.ceil(totalWeight - 5);
      cost += additionalWeight * 1000;
    }
    return cost;
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await orderService.getAll();
      setOrders(response.data.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await orderService.updateStatus(orderId, newStatus);
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      alert('تم تحديث حالة الطلب');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('فشل تحديث الطلب');
    }
  };

  const handleViewDetails = async (orderId) => {
    setSelectedOrder(orderId);
    setDetailsLoading(true);
    try {
      const response = await orderService.getAdminById(orderId);
      const orderData = response.data.data;
      setOrderDetails(orderData);
      // Calculate delivery price based on product weights
      const calculatedDeliveryPrice = calculateDeliveryPrice(orderData.items || []);
      setDeliveryPrice(calculatedDeliveryPrice);
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert('فشل تحميل تفاصيل الطلب');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setSelectedOrder(null);
    setOrderDetails(null);
    setEditingItems(false);
    setShowProductSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    setDeliveryPrice(0);
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await productService.search(query);
      setSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddProductToOrder = (product) => {
    // Check if product already exists in order
    const existingItem = orderDetails.items.find(item => item.product_id === product.id);
    
    if (existingItem) {
      alert('هذا المنتج موجود بالفعل في الطلب');
      return;
    }

    // Add new item with temporary id (will be created on save)
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      part_number: product.sku,
      price: product.price,
      quantity: 1,
      image_url: product.image_url,
      weight: product.weight || 1, // Include weight for delivery calculation
      isNew: true // Mark as new item
    };

    const updatedItems = [...orderDetails.items, newItem];
    
    const itemsTotal = updatedItems.reduce((sum, item) => 
      sum + (parseFloat(item.price) * item.quantity), 0
    );
    
    const newDeliveryPrice = calculateDeliveryPrice(updatedItems);
    const newTotal = itemsTotal + newDeliveryPrice;
    
    setOrderDetails({
      ...orderDetails,
      items: updatedItems,
      total_amount: newTotal
    });
    setDeliveryPrice(newDeliveryPrice);

    setShowProductSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleItemQuantityChange = (itemIndex, newQuantity) => {
    const quantity = parseInt(newQuantity);
    if (quantity < 1) return;
    
    const updatedItems = [...orderDetails.items];
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], quantity };
    
    const itemsTotal = updatedItems.reduce((sum, item) => 
      sum + (parseFloat(item.price) * item.quantity), 0
    );
    
    const newDeliveryPrice = calculateDeliveryPrice(updatedItems);
    const newTotal = itemsTotal + newDeliveryPrice;
    
    setOrderDetails({
      ...orderDetails,
      items: updatedItems,
      total_amount: newTotal
    });
    setDeliveryPrice(newDeliveryPrice);
  };

  const handleItemPriceChange = (itemIndex, newPrice) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) return;
    
    const updatedItems = [...orderDetails.items];
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], price };
    
    const itemsTotal = updatedItems.reduce((sum, item) => 
      sum + (parseFloat(item.price) * item.quantity), 0
    );
    
    const newDeliveryPrice = calculateDeliveryPrice(updatedItems);
    const newTotal = itemsTotal + newDeliveryPrice;
    
    setOrderDetails({
      ...orderDetails,
      items: updatedItems,
      total_amount: newTotal
    });
    setDeliveryPrice(newDeliveryPrice);
  };

  const handleRemoveItem = (itemIndex) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العنصر؟')) return;
    
    const updatedItems = orderDetails.items.filter((_, index) => index !== itemIndex);
    
    const itemsTotal = updatedItems.reduce((sum, item) => 
      sum + (parseFloat(item.price) * item.quantity), 0
    );
    
    const newDeliveryPrice = calculateDeliveryPrice(updatedItems);
    const newTotal = itemsTotal + newDeliveryPrice;
    
    setOrderDetails({
      ...orderDetails,
      items: updatedItems,
      total_amount: newTotal
    });
    setDeliveryPrice(newDeliveryPrice);
  };

  const handleSaveOrderChanges = async () => {
    try {
      // Recalculate total to ensure accuracy
      const itemsTotal = orderDetails.items.reduce((sum, item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        return sum + (price * quantity);
      }, 0);
      
      const totalAmount = itemsTotal + deliveryPrice;
      
      console.log('Items total:', itemsTotal);
      console.log('Delivery price:', deliveryPrice);
      console.log('Total amount:', totalAmount);
      console.log('Order items:', orderDetails.items);

      await orderService.updateOrderItems(orderDetails.id, {
        items: orderDetails.items.map(item => ({
          id: item.id, // Will be undefined for new items
          product_id: item.product_id,
          quantity: parseInt(item.quantity),
          price: item.price ? parseFloat(item.price) : null
        })),
        total_amount: totalAmount,
        delivery_price: deliveryPrice
      });
      
      alert('تم تحديث الطلب بنجاح');
      setEditingItems(false);
      fetchOrders();
      // Refresh order details
      handleViewDetails(orderDetails.id);
    } catch (error) {
      console.error('Error updating order:', error);
      alert('فشل تحديث الطلب: ' + (error.response?.data?.message || error.message));
    }
  };

  const parseShippingAddress = (addressStr) => {
    try {
      return JSON.parse(addressStr);
    } catch {
      return { address: addressStr };
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleAllOrders = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(order => order.id));
    }
  };

  const loadImageAsBase64 = async (imageUrl, maxWidth = 100) => {
    try {
      if (!imageUrl) return null;
      const fullUrl = imageUrl.startsWith('http') ? imageUrl : `https://api.iraq-parts.com${imageUrl}`;
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await fetch(fullUrl, { 
        signal: controller.signal,
        mode: 'cors'
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) return null;
      
      const blob = await response.blob();
      
      // Resize image to reduce memory usage
      return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions
          const scale = Math.min(maxWidth / img.width, maxWidth / img.height);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          
          // Draw resized image
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to base64 with maximum quality (PNG for best quality)
          const dataUrl = canvas.toDataURL('image/png');
          URL.revokeObjectURL(url);
          resolve(dataUrl);
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
        
        img.src = url;
      });
    } catch (error) {
      console.error('Error loading image:', error);
      return null;
    }
  };

  const exportOrdersToPDF = async () => {
    if (selectedOrders.length === 0) {
      alert('الرجاء تحديد طلب واحد على الأقل للتصدير');
      return;
    }

    if (selectedOrders.length > 5) {
      alert('لا يمكن تصدير أكثر من 5 طلبات في وقت واحد لتجنب مشاكل الذاكرة');
      return;
    }

    setExportLoading(true);
    
    try {
      // Create HTML content for all selected orders
      let htmlContent = `
        <html>
          <head>
            <meta charset="UTF-8">
            <style>
              body { font-family: Arial, sans-serif; direction: rtl; padding: 20px; }
              .page-break { page-break-after: always; }
              .order-container { margin-bottom: 40px; }
              h1 { text-align: center; color: #333; margin-bottom: 20px; font-size: 24px; }
              .info-section { margin-bottom: 20px; padding: 15px; background: #f5f5f5; border-radius: 5px; }
              .info-title { font-weight: bold; font-size: 16px; margin-bottom: 10px; color: #428bca; }
              .info-row { margin: 5px 0; font-size: 12px; }
              .info-label { font-weight: bold; color: #555; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th { background: #428bca; color: white; padding: 10px; text-align: center; font-size: 12px; border: 1px solid #ddd; }
              td { border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 11px; }
              tbody tr:not(.summary-row):not(.total-row):nth-child(even) { background: #f9f9f9; }
              .summary-row td { background: #f0f0f0 !important; font-weight: bold; font-size: 12px; }
              .total-row td { background: #428bca !important; color: white !important; font-weight: bold; font-size: 14px; }
              .product-image { width: 120px; height: 120px; object-fit: cover; border-radius: 4px; }
            </style>
          </head>
          <body>
      `;

      for (let i = 0; i < selectedOrders.length; i++) {
        const orderId = selectedOrders[i];
        try {
          // Fetch full order details
          const response = await orderService.getAdminById(orderId);
          const order = response.data.data;
          const address = parseShippingAddress(order.shipping_address);

          // Add page break between orders (except first one)
          if (i > 0) {
            htmlContent += `<div class="page-break"></div>`;
          }

          htmlContent += `
            <div class="order-container">
              <h1>تفاصيل الطلب - ${order.order_code || `#${order.id}`}</h1>
          `;

          // Shipping Address
          if (address) {
            htmlContent += `
              <div class="info-section">
                <div class="info-title">عنوان التوصيل</div>
            `;
            if (address.name) {
              htmlContent += `<div class="info-row"><span class="info-label">الاسم:</span> ${address.name}</div>`;
            }
            if (address.phone) {
              htmlContent += `<div class="info-row"><span class="info-label">الهاتف:</span> ${address.phone}</div>`;
            }
            if (address.city) {
              htmlContent += `<div class="info-row"><span class="info-label">المدينة:</span> ${address.city}</div>`;
            }
            if (address.address) {
              htmlContent += `<div class="info-row"><span class="info-label">العنوان:</span> ${address.address}</div>`;
            }
            if (address.notes) {
              htmlContent += `<div class="info-row"><span class="info-label">ملاحظات:</span> ${address.notes}</div>`;
            }
            htmlContent += `</div>`;
          }

          // Calculate totals
          const itemsTotal = (order.items || []).reduce((sum, item) => 
            sum + ((parseFloat(item.price) || 0) * item.quantity), 0
          );

          // Load product images
          const imagePromises = (order.items || []).map(item => 
            item.image_url ? loadImageAsBase64(item.image_url, 600).catch(() => null) : Promise.resolve(null)
          );
          const images = await Promise.all(imagePromises);

          // Order Items Table with totals inside
          htmlContent += `
            <div class="info-section">
              <div class="info-title">المنتجات</div>
              <table>
                <thead>
                  <tr>
                    <th>الصورة</th>
                    <th>اسم المنتج</th>
                    <th>رقم القطعة</th>
                    <th>الكمية</th>
                    <th>السعر</th>
                    <th>المجموع</th>
                  </tr>
                </thead>
                <tbody>
          `;

          (order.items || []).forEach((item, index) => {
            const imageTag = images[index] 
              ? `<img src="${images[index]}" class="product-image" alt="صورة المنتج" />`
              : '';
            
            htmlContent += `
              <tr>
                <td>${imageTag}</td>
                <td>${item.product_name || ''}</td>
                <td>${item.part_number || ''}</td>
                <td>${item.quantity || 0}</td>
                <td>${(parseFloat(item.price) || 0).toLocaleString()} د.ع</td>
                <td>${((parseFloat(item.price) || 0) * item.quantity).toLocaleString()} د.ع</td>
              </tr>
            `;
          });

          // Add summary rows to the table
          htmlContent += `
                  <tr class="summary-row">
                    <td colspan="5" style="text-align: right; padding-right: 20px;">مجموع المنتجات:</td>
                    <td>${itemsTotal.toLocaleString()} د.ع</td>
                  </tr>
                  <tr class="summary-row">
                    <td colspan="5" style="text-align: right; padding-right: 20px;">التوصيل:</td>
                    <td>${(order.delivery_price || 5000).toLocaleString()} د.ع</td>
                  </tr>
                  <tr class="total-row">
                    <td colspan="5" style="text-align: right; padding-right: 20px;">المبلغ الإجمالي:</td>
                    <td>${(parseFloat(order.total_amount) || 0).toLocaleString()} د.ع</td>
                  </tr>
                </tbody>
              </table>
            </div>
          `;

          htmlContent += `</div>`; // Close order-container
        } catch (orderError) {
          console.error(`Error processing order ${orderId}:`, orderError);
        }
      }

      htmlContent += `
          </body>
        </html>
      `;

      // Create temporary element and add to body
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);

      // Wait a moment for rendering
      await new Promise(resolve => setTimeout(resolve, 100));

      // Generate PDF options
      const opt = {
        margin: [10, 10, 10, 10],
        filename: selectedOrders.length === 1 
          ? `order-${orders.find(o => o.id === selectedOrders[0])?.order_code || selectedOrders[0]}.pdf`
          : `orders-export-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 1.0 },
        html2canvas: { 
          scale: 4,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          logging: false,
          imageTimeout: 0,
          removeContainer: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: false
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      // Generate and save PDF
      await html2pdf().from(tempDiv).set(opt).save();

      // Cleanup
      document.body.removeChild(tempDiv);

      alert(`تم تصدير ${selectedOrders.length} طلب بنجاح`);
      setSelectedOrders([]);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('فشل تصدير الطلبات: ' + error.message);
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'معلق',
      processing: 'قيد المعالجة',
      shipped: 'تم الشحن',
      delivered: 'تم التوصيل',
      cancelled: 'ملغي'
    };
    return statusMap[status] || status;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'معلق', color: 'bg-yellow-100 text-yellow-800' },
      processing: { text: 'قيد المعالجة', color: 'bg-blue-100 text-blue-800' },
      shipped: { text: 'تم الشحن', color: 'bg-purple-100 text-purple-800' },
      delivered: { text: 'تم التوصيل', color: 'bg-green-100 text-green-800' },
      cancelled: { text: 'ملغي', color: 'bg-red-100 text-red-800' }
    };
    const statusInfo = statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 text-xs rounded ${statusInfo.color}`}>{statusInfo.text}</span>;
  };

  if (loading) return <Loading />;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold">إدارة الطلبات</h1>
        {selectedOrders.length > 0 && (
          <button
            onClick={exportOrdersToPDF}
            disabled={exportLoading}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            {exportLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>جاري التصدير...</span>
              </>
            ) : (
              <>
                <span>📄</span>
                <span>تصدير ({selectedOrders.length})</span>
              </>
            )}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-center">
                <input
                  type="checkbox"
                  checked={selectedOrders.length === orders.length && orders.length > 0}
                  onChange={toggleAllOrders}
                  className="w-4 h-4 cursor-pointer"
                />
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عرض</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الإجراءات</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الحالة</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">المبلغ</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">العناصر</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">العميل</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">التاريخ</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">كود الطلب</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {orders.map(order => (
              <tr key={order.id} className={selectedOrders.includes(order.id) ? 'bg-blue-50' : ''}>
                <td className="px-6 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => toggleOrderSelection(order.id)}
                    className="w-4 h-4 cursor-pointer"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleViewDetails(order.id)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    عرض التفاصيل
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="pending">معلق</option>
                    <option value="processing">قيد المعالجة</option>
                    <option value="shipped">تم الشحن</option>
                    <option value="delivered">تم التوصيل</option>
                    <option value="cancelled">ملغي</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(order.status)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.total_amount?.toLocaleString()} د.ع</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.item_count}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {order.first_name} {order.last_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(order.created_at).toLocaleDateString('ar-SA')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.order_code || `#${order.id}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeDetailsModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} dir="rtl">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">تفاصيل الطلب {orderDetails?.order_code || `#${selectedOrder}`}</h2>
              <button
                onClick={closeDetailsModal}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {detailsLoading ? (
              <div className="p-8 text-center">
                <Loading />
              </div>
            ) : orderDetails ? (
              <div className="p-6 space-y-6">
                {/* Order Status */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-2">حالة الطلب</h3>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(orderDetails.status)}
                    <span className="text-sm text-gray-600">
                      تم الإنشاء: {new Date(orderDetails.created_at).toLocaleString('ar-SA')}
                    </span>
                  </div>
                </div>

                {/* Shipping Address */}
                {orderDetails.shipping_address && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3">عنوان التوصيل</h3>
                    {(() => {
                      const address = parseShippingAddress(orderDetails.shipping_address);
                      return (
                        <div className="space-y-2 text-sm">
                          {address.name && (
                            <div>
                              <span className="font-medium text-gray-700">الاسم:</span>
                              <span className="mr-2">{address.name}</span>
                            </div>
                          )}
                          {address.phone && (
                            <div>
                              <span className="font-medium text-gray-700">الهاتف:</span>
                              <span className="mr-2">{address.phone}</span>
                            </div>
                          )}
                          {address.city && (
                            <div>
                              <span className="font-medium text-gray-700">المدينة:</span>
                              <span className="mr-2">{address.city}</span>
                            </div>
                          )}
                          {address.address && (
                            <div>
                              <span className="font-medium text-gray-700">العنوان:</span>
                              <span className="mr-2">{address.address}</span>
                            </div>
                          )}
                          {address.notes && (
                            <div>
                              <span className="font-medium text-gray-700">ملاحظات:</span>
                              <span className="mr-2">{address.notes}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Order Items */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold">المنتجات</h3>
                    <div className="flex gap-2">
                      {editingItems && (
                        <button
                          onClick={() => setShowProductSearch(!showProductSearch)}
                          className="px-4 py-1 rounded text-sm bg-green-500 text-white hover:bg-green-600"
                        >
                          {showProductSearch ? 'إخفاء البحث' : '+ إضافة منتج'}
                        </button>
                      )}
                      <button
                        onClick={() => setEditingItems(!editingItems)}
                        className={`px-4 py-1 rounded text-sm ${
                          editingItems 
                            ? 'bg-gray-500 text-white hover:bg-gray-600' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        {editingItems ? 'إلغاء التعديل' : 'تعديل المنتجات'}
                      </button>
                    </div>
                  </div>

                  {/* Product Search */}
                  {showProductSearch && (
                    <div className="mb-4 bg-white rounded-lg p-4 border border-blue-200">
                      <h4 className="font-semibold mb-2">البحث عن منتج</h4>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          searchProducts(e.target.value);
                        }}
                        placeholder="ابحث عن منتج بالاسم أو رقم القطعة..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {searchLoading && (
                        <div className="mt-2 text-sm text-gray-500">جاري البحث...</div>
                      )}
                      {searchResults.length > 0 && (
                        <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded">
                          {searchResults.map((product) => (
                            <div
                              key={product.id}
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center gap-3"
                              onClick={() => handleAddProductToOrder(product)}
                            >
                              {product.image_url && (
                                <img
                                  src={product.image_url.startsWith('http') ? product.image_url : `https://api.iraq-parts.com${product.image_url}`}
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              )}
                              <div className="flex-1">
                                <div className="font-medium">{product.name}</div>
                                {product.sku && (
                                  <div className="text-sm text-gray-600">رقم القطعة: {product.sku}</div>
                                )}
                                <div className="text-sm text-gray-600">
                                  المخزون: {product.stock_quantity}
                                </div>
                              </div>
                              <div className="text-left">
                                <div className="font-semibold">{product.price?.toLocaleString()} د.ع</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                        <div className="mt-2 text-sm text-gray-500 text-center py-2">
                          لم يتم العثور على منتجات
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3">
                    {orderDetails.items && orderDetails.items.length > 0 ? (
                      orderDetails.items.map((item, index) => (
                        <div key={index} className="bg-white rounded p-3 flex items-center gap-4">
                          {item.image_url && (
                            <img
                              src={item.image_url.startsWith('http') ? item.image_url : `https://api.iraq-parts.com${item.image_url}`}
                              alt={item.product_name}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold">{item.product_name}</h4>
                            {item.part_number && (
                              <p className="text-sm text-gray-600">رقم القطعة: {item.part_number}</p>
                            )}
                            {editingItems ? (
                              <div className="flex items-center gap-4 mt-2">
                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-600">الكمية:</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-600">السعر:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.price || 0}
                                    onChange={(e) => handleItemPriceChange(index, e.target.value)}
                                    className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                  <span className="text-xs text-gray-500">د.ع</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-600">الكمية: {item.quantity}</p>
                            )}
                          </div>
                          <div className="text-left">
                            <p className="font-semibold">{(parseFloat(item.price) || 0).toLocaleString()} د.ع</p>
                            <p className="text-sm text-gray-600">
                              المجموع: {((parseFloat(item.price) || 0) * item.quantity).toLocaleString()} د.ع
                            </p>
                          </div>
                          {editingItems && (
                            <button
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-600 hover:text-red-800 px-2 py-1 text-sm"
                              title="حذف"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">لا توجد منتجات</p>
                    )}
                  </div>
                  {editingItems && orderDetails.items.length > 0 && (
                    <button
                      onClick={handleSaveOrderChanges}
                      className="mt-4 w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-semibold"
                    >
                      حفظ التغييرات
                    </button>
                  )}
                </div>

                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">ملخص الطلب</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">طريقة الدفع:</span>
                      <span className="font-medium">
                        {orderDetails.payment_method === 'cash_on_delivery' ? 'الدفع عند الاستلام' : orderDetails.payment_method}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>المبلغ الإجمالي:</span>
                      <span>{(parseFloat(orderDetails.total_amount) || 0).toLocaleString()} د.ع</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <select
                    value={orderDetails.status}
                    onChange={(e) => {
                      handleStatusChange(orderDetails.id, e.target.value);
                      setOrderDetails({ ...orderDetails, status: e.target.value });
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
                  >
                    <option value="pending">معلق</option>
                    <option value="processing">قيد المعالجة</option>
                    <option value="shipped">تم الشحن</option>
                    <option value="delivered">تم التوصيل</option>
                    <option value="cancelled">ملغي</option>
                  </select>
                  <button
                    onClick={closeDetailsModal}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    إغلاق
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                فشل تحميل تفاصيل الطلب
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
